import {
  ACTIVE_REQUIRED_FIELDS,
  ACTIVE_MAILING_FIELDS,
  getConditionalMailingFields,
  type BienDraft,
} from "@/lib/bienPrepValidation";

export type CreateFieldLevel = "required" | "mailing" | "support";
export type CreateFieldSection =
  | "identification" | "dates" | "localisation" | "interieur"
  | "annexes" | "financier" | "energie" | "els";

export interface CreateFormField {
  key: string;
  section: CreateFieldSection;
  level: CreateFieldLevel;
}

/** Layout curé : ordre + section de chaque champ. Les conditionnels
 *  (cave/parking/garage n°/loyer) ne sortent que si actifs dans le draft. */
const LAYOUT: { key: string; section: CreateFieldSection }[] = [
  { key: "referenceInterne", section: "identification" },
  { key: "projet", section: "identification" },
  { key: "typeBien", section: "identification" },
  // `transaction` volontairement ABSENTE : la création est contextuelle depuis
  // le catalogue (location ici, vente dans BienVenteCreateFormPage) et la
  // valeur est figée dans INITIAL_DRAFT. Elle reste dans
  // ACTIVE_REQUIRED_FIELDS — toujours remplie, donc jamais signalée manquante.
  // `entreeGestion` retirée (2026-09-15, plan « deux cycles ») : l'existence
  // d'un cycle de sortie est un geste explicite (« Ouvrir un cycle »), plus
  // une classification à la création. `collaborateurSrs`/`renonRecuLe`
  // restent saisissables ici mais ne sont plus requis (champs du cycle).
  { key: "collaborateurSrs", section: "identification" },
  { key: "companyContract", section: "identification" },
  { key: "meuble", section: "identification" },
  { key: "seraDispoPour", section: "dates" },
  { key: "renonRecuLe", section: "dates" },
  { key: "finDeContrat", section: "dates" },
  { key: "codePostal", section: "localisation" },
  { key: "ville", section: "localisation" },
  { key: "adresse", section: "localisation" },
  { key: "etage", section: "localisation" },
  { key: "nbChambres", section: "interieur" },
  { key: "nbSdb", section: "interieur" },
  { key: "typeSol", section: "interieur" },
  { key: "caveDisponible", section: "annexes" },
  { key: "caveNumero", section: "annexes" },
  { key: "parkingDisponible", section: "annexes" },
  { key: "parkingNumero", section: "annexes" },
  { key: "parkingLoyerMensuel", section: "annexes" },
  { key: "garageDisponible", section: "annexes" },
  { key: "garageNumero", section: "annexes" },
  { key: "garageLoyerMensuel", section: "annexes" },
  { key: "loyerMensuel", section: "financier" },
  { key: "chargesMensuelles", section: "financier" },
  { key: "chargesIncluses", section: "financier" },
  { key: "classePeb", section: "energie" },
  { key: "pebFinLe", section: "energie" },
  { key: "consoKwh", section: "energie" },
  { key: "prestataireEls", section: "els" },
];

const REQUIRED = new Set<string>(ACTIVE_REQUIRED_FIELDS);
const MAILING = new Set<string>(ACTIVE_MAILING_FIELDS);
const CONDITIONAL_KEYS = new Set<string>([
  "caveNumero", "parkingNumero", "parkingLoyerMensuel", "garageNumero", "garageLoyerMensuel",
]);

export function getCreateFormFields(draft: BienDraft): CreateFormField[] {
  const activeConditionals = new Set(getConditionalMailingFields(draft));
  const out: CreateFormField[] = [];
  for (const item of LAYOUT) {
    if (CONDITIONAL_KEYS.has(item.key)) {
      if (!activeConditionals.has(item.key)) continue;
      out.push({ key: item.key, section: item.section, level: "mailing" });
      continue;
    }
    const level: CreateFieldLevel = REQUIRED.has(item.key)
      ? "required"
      : MAILING.has(item.key)
        ? "mailing"
        : "support";
    out.push({ key: item.key, section: item.section, level });
  }
  return out;
}
